import { Core, Decorative, TopShop } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web--source/common/transform-images";
import Color from "@faire/web--source/slate/Color";
import { Pill } from "@faire/web--source/slate/Filter/styled";
import { Insider } from "@faire/web--source/slate/icons/InsiderIcon";
import {
  ActiveTrophySecondary,
  TrophyIcon,
} from "@faire/web--source/slate/icons/Trophy";
import { Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { DefaultTheme } from "@faire/web--source/slate/Theme";
import { FaireMarketTheme } from "@faire/web--source/slate/Theme/EventsTheme";
import { ThemeProvider } from "@faire/web--source/slate/Theme/ThemeProvider";
import { Typography } from "@faire/web--source/slate/Typography";
import { IFilterSection } from "@faire/web-api--source/indigofair/data/IFilterSection";
import React from "react";
import styled, { css } from "styled-components";

import { FaireMarketIcon } from "@faire/retailer/features/FaireMarkets/FaireMarketIcon";
import { handleKeyDown } from "@faire/retailer/lib/handleKeyDown";
import { getSettingElevateTopShopV2 } from "@faire/retailer/settings/getSettingElevateTopShopV2";
import { MarketStore } from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";

export interface IProps {
  section: IFilterSection;
  onChange: (
    section: IFilterSection,
    filterKey: string,
    value: boolean
  ) => void;
  className?: string;
  showLoading?: boolean;
  disabled?: boolean;
}

export const FilterToggle: React.FC<IProps> = ({
  className,
  section,
  onChange,
  showLoading = false,
  disabled = false,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const option = section.options.length > 0 ? section.options[0] : undefined;
  const isInsider = section.field_name === IFilterSection.FieldName.INSIDER;
  const isTopShop = section.field_name === IFilterSection.FieldName.TOP_SHOP;
  const isSelected = option?.is_selected ?? false;
  const isDisabled = disabled && !isSelected;
  const isMarketSpecials =
    section.field_name === IFilterSection.FieldName.MAKER_SPECIAL &&
    MarketStore.get().isLive;

  const isSSR = useIsSSR();
  if (
    (!showLoading && option?.key === undefined) ||
    ((option?.results_count ?? 1) === 0 && !isSelected)
  ) {
    return null;
  }

  const onToggle = () => {
    if (option !== undefined && !isDisabled) {
      const newIsSelected = !option?.is_selected;
      const updatedSection = {
        ...section,
        options: section.options.map((option, index) =>
          index === 0 ? { ...option, is_selected: newIsSelected } : option
        ),
      };
      onChange(updatedSection, option.key ?? "", newIsSelected);
    }
  };

  const renderDisplayName = () => {
    if (isInsider) {
      return (
        <StyledText
          id={`toggle-${option?.key ?? ""}`}
          color={isSelected ? Core.text.inverse : Decorative.shade.teal}
          variant="paragraphSansMedium"
        >
          {section.display_name}
        </StyledText>
      );
    } else if (isMarketSpecials) {
      return (
        <Row align="center">
          <FaireMarketIcon size="16px" />
          <Spacer width="1x" />
          <StyledText
            id={`toggle-${option?.key ?? ""}`}
            variant="paragraphSansRegular"
            color="inherit"
          >
            {section.display_name}
          </StyledText>
        </Row>
      );
    }

    return (
      <StyledText
        id={`toggle-${option?.key ?? ""}`}
        variant="paragraphSansRegular"
        color={isSelected ? Core.text.inverse : Core.text.default}
      >
        {section.display_name}
      </StyledText>
    );
  };

  const getImage = () => {
    if (isInsider) {
      return (
        <Insider
          small
          color={isSelected ? Core.icon.inverse : Decorative.icon.teal}
        />
      );
    } else if (isTopShop) {
      if (isSelected) {
        return (
          <ActiveTrophySecondary
            color={TopShop.shade.green}
            fill={TopShop.shade.green}
            fontSize="medium"
            titleAccess=""
          />
        );
      } else {
        return (
          <TrophyIcon
            active
            fontSize="medium"
            color={Core.icon.inverse}
            fill={Core.icon.inverse}
            titleAccess=""
          />
        );
      }
    } else {
      if (option?.image) {
        return (
          <ToggleImage
            width={16}
            src={optimizeImageUrl(option.image, isSSR, {
              width: 16,
            })}
          />
        );
      }
    }
    return null;
  };

  const getPillStyles = () => {
    const styles: React.CSSProperties = {};
    if (isSelected) {
      styles.color = Core.text.inverse;
      styles.backgroundColor = Core.surface.default;
      if (isInsider) {
        styles.backgroundColor = Decorative.shade.teal;
      } else if (isTopShop) {
        styles.backgroundColor = TopShop.shade.green;
      }
    } else if (showLoading) {
      styles.backgroundColor = Core.surface.subdued;
    } else {
      styles.border = `1px solid ${Core.border.muted}`;
      styles.backgroundColor = Core.surface.inverse;
    }
    if (isDisabled) {
      styles.backgroundColor = Core.surface.subdued;
      styles.cursor = `not-allowed`;
    }
    return styles;
  };

  const getHoverColor = () => {
    if (isInsider && !isSelected) {
      return Decorative.shade.teal;
    } else if (isTopShop && !isSelected) {
      return TopShop.shade.green;
    } else if (!isSelected && !showLoading && !isDisabled) {
      return Core.border.default;
    }
    return undefined;
  };

  return (
    <Wrapper
      className={className}
      role="button"
      aria-pressed={isSelected}
      onClick={onToggle}
      onKeyDown={handleKeyDown(onToggle)}
      tabIndex={isDisabled ? -1 : 0}
      aria-label={
        section.display_name ??
        strictLocalize({
          defaultMessage: "Loading",
          description: {
            text: "Waiting for the data to be loaded",
          },
        })
      }
      style={{
        opacity: isDisabled ? 0.5 : 1,
      }}
    >
      {isTopShop && !getSettingElevateTopShopV2() ? null : (
        <ThemeProvider
          theme={isMarketSpecials ? FaireMarketTheme : DefaultTheme}
        >
          <StyledPill
            isOpen={false}
            alwaysShowCloseButton={false}
            hasFilterSelected={isSelected}
            style={getPillStyles()}
            $hoverColor={getHoverColor()}
          >
            {getImage()}
            <Spacer width={4} />
            {showLoading ? <LoadingTextPlaceholder /> : null}
            {renderDisplayName()}
          </StyledPill>
        </ThemeProvider>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: max-content;
  &:focus-visible {
    border-radius: 40px;
  }
`;

const ToggleImage = styled.img`
  margin-left: 3px;
`;

const StyledText = styled(Typography)`
  &&& {
    margin-left: 6px;
    margin-right: 6px;
    width: max-content;
  }
`;

const StyledPill = styled(Pill)<{
  $hoverColor?: Color;
}>`
  padding: ${getSpacing(0, 10)};

  ${({ $hoverColor }) =>
    $hoverColor
      ? css`
          &:hover {
            border: 1px solid ${$hoverColor} !important;
          }
        `
      : null}
`;

const LoadingTextPlaceholder = styled.div`
  height: 16px;
  width: 115px;
  background-color: ${Core.surface.inverse};
`;
